// src/components/admin/TabAliados.js
// Tab del AdminDashboard para gestionar aliados comerciales.
// Ver docs/aliados.md (backend) para el modelo completo.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Grid, Stack, Chip, Alert, Button,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, TablePagination,
  IconButton, Tooltip, CircularProgress, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  listarAliados, getAliado, getTiposNegocio,
  aprobarAliado, rechazarAliado, toggleMarketplace, togglePOS,
  toggleActivoAliado, eliminarAliado,
} from '../../services/aliadosApi';
import UbicacionMapaPicker from '../UbicacionMapaPicker';
import VisorDocumentoSeguro from '../VisorDocumentoSeguro';
import { getUrlFirmadaDocumento } from '../../services/authApi';
import { esNitTest } from '../../services/testAliadosApi';

// Colores por estado
const ESTADO_META = {
  BORRADOR:             { color: '#888',    bg: '#f5f5f5', label: 'Borrador' },
  PENDIENTE_APROBACION: { color: '#e65100', bg: '#fff3e0', label: 'Pendiente' },
  APROBADO:             { color: '#2e7d32', bg: '#e8f5e9', label: 'Aprobado' },
  RECHAZADO:            { color: '#c62828', bg: '#ffebee', label: 'Rechazado' },
  SUSPENDIDO:           { color: '#6a1b9a', bg: '#f3e5f5', label: 'Suspendido' },
};

const chipEstado = (estado) => {
  const m = ESTADO_META[estado] || { color: '#555', bg: '#eee', label: estado };
  return <Chip label={m.label} size="small" sx={{ bgcolor: m.bg, color: m.color, fontWeight: 700 }} />;
};

export default function TabAliados({ onToast }) {
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(0);      // 0-indexed en MUI, se envía +1 al backend
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading]   = useState(false);
  const [tiposNegocio, setTiposNegocio] = useState([]);

  // Filtros
  const [fEstado, setFEstado]           = useState('');
  const [fTipo,   setFTipo]             = useState('');
  const [fBuscar, setFBuscar]           = useState('');
  const [fVisible, setFVisible]         = useState('');   // '', 'true', 'false'

  // Detalle
  const [detalleAbierto, setDetalleAbierto] = useState(false);
  const [detalle, setDetalle]               = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalleAccion, setDetalleAccion]   = useState(false);   // muestra spinner en botones
  // Visor de documentos (reutiliza el componente existente que ya maneja
  // autenticación por JWT, blob URL efímero, zoom, rotación y descarga).
  const [visor, setVisor] = useState({ open: false, url: null, titulo: '', nombre: '' });

  // Rechazo dialog
  const [rechazoAbierto, setRechazoAbierto] = useState(false);
  const [motivoRechazo, setMotivoRechazo]   = useState('');

  // Eliminar dialog (confirmación fuerte con texto)
  const [eliminarAbierto, setEliminarAbierto] = useState(false);
  const [textoConfirmEliminar, setTextoConfirmEliminar] = useState('');

  // Motivo dialog genérico (activar/desactivar aliado, marketplace, POS)
  const [motivoAbierto, setMotivoAbierto] = useState(false);
  const [motivoTexto, setMotivoTexto] = useState('');
  const [motivoConfig, setMotivoConfig] = useState(null);
    // { titulo, accionLabel, color, placeholder, onConfirmar: (motivo)=>Promise }

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listarAliados({
        estado: fEstado || undefined,
        visibleMarketplace: fVisible === '' ? undefined : fVisible === 'true',
        tipoNegocioId: fTipo || undefined,
        buscar: fBuscar.trim() || undefined,
        page: page + 1,
        pageSize,
      });
      setRows(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      onToast?.(e.message || 'No se pudo cargar el listado.', 'error');
    } finally {
      setLoading(false);
    }
  }, [fEstado, fVisible, fTipo, fBuscar, page, pageSize, onToast]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    getTiposNegocio(true).then(setTiposNegocio).catch(() => setTiposNegocio([]));
  }, []);

  // Conteo por estado (best-effort: solo lo que se ve, no golpea backend extra)
  const conteos = useMemo(() => {
    const c = { PENDIENTE_APROBACION: 0, APROBADO: 0, RECHAZADO: 0, SUSPENDIDO: 0 };
    rows.forEach(r => { if (c[r.estadoAprobacion] !== undefined) c[r.estadoAprobacion]++; });
    return c;
  }, [rows]);

  const abrirDetalle = async (id) => {
    setDetalleAbierto(true);
    setDetalleLoading(true);
    setDetalle(null);
    try {
      const a = await getAliado(id);
      setDetalle(a);
    } catch (e) {
      onToast?.(e.message || 'No se pudo cargar el detalle.', 'error');
      setDetalleAbierto(false);
    } finally {
      setDetalleLoading(false);
    }
  };

  const cerrarDetalle = () => {
    setDetalleAbierto(false);
    setDetalle(null);
    setMotivoRechazo('');
  };

  const accionAprobar = async () => {
    if (!detalle) return;
    setDetalleAccion(true);
    try {
      await aprobarAliado(detalle.aliadoID);
      onToast?.(`Aliado ${detalle.codigo} aprobado.`, 'success');
      cerrarDetalle();
      cargar();
    } catch (e) {
      onToast?.(e.message || 'No se pudo aprobar.', 'error');
    } finally {
      setDetalleAccion(false);
    }
  };

  const accionRechazar = async () => {
    if (!detalle) return;
    if (!motivoRechazo.trim()) {
      onToast?.('Escribe el motivo del rechazo.', 'warning');
      return;
    }
    setDetalleAccion(true);
    try {
      await rechazarAliado(detalle.aliadoID, motivoRechazo.trim());
      onToast?.(`Aliado ${detalle.codigo} rechazado.`, 'success');
      setRechazoAbierto(false);
      cerrarDetalle();
      cargar();
    } catch (e) {
      onToast?.(e.message || 'No se pudo rechazar.', 'error');
    } finally {
      setDetalleAccion(false);
    }
  };

  const accionToggleMarketplace = async (motivo) => {
    if (!detalle) return;
    setDetalleAccion(true);
    try {
      const nuevo = !detalle.visibleMarketplace;
      await toggleMarketplace(detalle.aliadoID, nuevo, motivo);
      onToast?.(nuevo ? 'Marketplace activado para este aliado.' : 'Marketplace desactivado para este aliado.', 'success');
      setDetalle({ ...detalle, visibleMarketplace: nuevo });
      cargar();
    } catch (e) {
      onToast?.(e.message || 'No se pudo cambiar el marketplace.', 'error');
    } finally {
      setDetalleAccion(false);
    }
  };

  const accionTogglePOS = async (motivo) => {
    if (!detalle) return;
    setDetalleAccion(true);
    try {
      const nuevo = !detalle.visiblePOS;
      await togglePOS(detalle.aliadoID, nuevo, motivo);
      onToast?.(nuevo ? 'POS activado para este aliado.' : 'POS desactivado para este aliado.', 'success');
      setDetalle({ ...detalle, visiblePOS: nuevo });
      cargar();
    } catch (e) {
      onToast?.(e.message || 'No se pudo cambiar el POS.', 'error');
    } finally {
      setDetalleAccion(false);
    }
  };

  const accionToggleActivo = async (motivo) => {
    if (!detalle) return;
    setDetalleAccion(true);
    try {
      const estaActivo = detalle.estadoAprobacion === 'APROBADO';
      const nuevo = !estaActivo; // true = activar, false = suspender
      const r = await toggleActivoAliado(detalle.aliadoID, nuevo, motivo);
      onToast?.(
        nuevo ? `Aliado ${detalle.codigo} activado.` : `Aliado ${detalle.codigo} suspendido.`,
        'success'
      );
      setDetalle({
        ...detalle,
        estadoAprobacion:   r.estadoAprobacion   ?? (nuevo ? 'APROBADO' : 'SUSPENDIDO'),
        visibleMarketplace: r.visibleMarketplace ?? detalle.visibleMarketplace,
        visiblePOS:         r.visiblePOS         ?? detalle.visiblePOS,
      });
      cargar();
    } catch (e) {
      onToast?.(e.message || 'No se pudo cambiar el estado.', 'error');
    } finally {
      setDetalleAccion(false);
    }
  };

  // Dialog genérico de "Motivo" — se abre antes de ejecutar cualquier
  // acción de activar/desactivar (aliado, marketplace, POS). El motivo
  // digitado (opcional) se envía al backend para quedar en auditoría.
  const abrirMotivo = (config) => {
    setMotivoTexto('');
    setMotivoConfig(config);
    setMotivoAbierto(true);
  };

  const confirmarMotivo = async () => {
    if (!motivoConfig?.onConfirmar) return;
    const motivo = motivoTexto.trim() || null;
    await motivoConfig.onConfirmar(motivo);
    setMotivoAbierto(false);
  };

  const pedirMotivoToggleActivo = () => {
    const estaActivo = detalle?.estadoAprobacion === 'APROBADO';
    abrirMotivo({
      titulo:      estaActivo ? 'Suspender aliado' : 'Reactivar aliado',
      accionLabel: estaActivo ? 'Suspender'        : 'Reactivar',
      color:       estaActivo ? 'warning'          : 'success',
      placeholder: estaActivo
        ? '¿Por qué suspendes este aliado? (opcional)'
        : '¿Por qué lo reactivas? (opcional)',
      onConfirmar: accionToggleActivo,
    });
  };

  const pedirMotivoToggleMarketplace = () => {
    const activo = detalle?.visibleMarketplace;
    abrirMotivo({
      titulo:      activo ? 'Desactivar marketplace' : 'Activar marketplace',
      accionLabel: activo ? 'Desactivar'             : 'Activar',
      color:       activo ? 'warning'                : 'success',
      placeholder: activo
        ? 'Motivo para desactivar el marketplace (opcional)'
        : 'Motivo para activar el marketplace (opcional)',
      onConfirmar: accionToggleMarketplace,
    });
  };

  const pedirMotivoTogglePOS = () => {
    const activo = detalle?.visiblePOS;
    abrirMotivo({
      titulo:      activo ? 'Desactivar POS' : 'Activar POS',
      accionLabel: activo ? 'Desactivar'    : 'Activar',
      color:       activo ? 'warning'       : 'success',
      placeholder: activo
        ? 'Motivo para desactivar el POS (opcional)'
        : 'Motivo para activar el POS (opcional)',
      onConfirmar: accionTogglePOS,
    });
  };

  const abrirEliminar = () => {
    setTextoConfirmEliminar('');
    setEliminarAbierto(true);
  };

  const accionEliminar = async () => {
    if (!detalle) return;
    setDetalleAccion(true);
    try {
      const r = await eliminarAliado(detalle.aliadoID);
      const detalles = r.vinculosBorrados > 0
        ? ` (${r.vinculosBorrados} vínculo(s) borrado(s), ${r.usuariosSinRolAliado} usuario(s) perdió rol Aliado)`
        : '';
      onToast?.(`Aliado ${detalle.codigo} eliminado del entorno ecolab${detalles}.`, 'success');
      setEliminarAbierto(false);
      cerrarDetalle();
      cargar();
    } catch (e) {
      onToast?.(e.message || 'No se pudo eliminar.', 'error');
    } finally {
      setDetalleAccion(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <StorefrontOutlinedIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a4d2e' }}>Aliados comerciales</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Recargar">
          <IconButton onClick={cargar} disabled={loading}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      {/* Chips de conteo rápidos (filtros por estado) */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        {[
          { key: '', label: `Todos` },
          { key: 'PENDIENTE_APROBACION', label: `Pendientes${conteos.PENDIENTE_APROBACION ? ` (${conteos.PENDIENTE_APROBACION})` : ''}` },
          { key: 'APROBADO', label: `Aprobados${conteos.APROBADO ? ` (${conteos.APROBADO})` : ''}` },
          { key: 'RECHAZADO', label: `Rechazados${conteos.RECHAZADO ? ` (${conteos.RECHAZADO})` : ''}` },
          { key: 'SUSPENDIDO', label: `Suspendidos${conteos.SUSPENDIDO ? ` (${conteos.SUSPENDIDO})` : ''}` },
        ].map(c => (
          <Chip key={c.key || 'todos'}
            label={c.label}
            variant={fEstado === c.key ? 'filled' : 'outlined'}
            color={fEstado === c.key ? 'success' : 'default'}
            onClick={() => { setPage(0); setFEstado(c.key); }}
            size="small"
          />
        ))}
      </Stack>

      {/* Filtros adicionales */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth size="small" label="Buscar (razón social, NIT, email)"
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#888' }} /> }}
              value={fBuscar}
              onChange={e => { setFBuscar(e.target.value); setPage(0); }}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de negocio</InputLabel>
              <Select value={fTipo} label="Tipo de negocio"
                onChange={e => { setFTipo(e.target.value); setPage(0); }}>
                <MenuItem value=""><em>Todos</em></MenuItem>
                {tiposNegocio.map(t => (
                  <MenuItem key={t.tipoNegocioID} value={t.tipoNegocioID}>{t.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Marketplace</InputLabel>
              <Select value={fVisible} label="Marketplace"
                onChange={e => { setFVisible(e.target.value); setPage(0); }}>
                <MenuItem value=""><em>Todos</em></MenuItem>
                <MenuItem value="true">Visibles</MenuItem>
                <MenuItem value="false">Ocultos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla */}
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
        {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>}
        {!loading && rows.length === 0 && (
          <Alert severity="info" sx={{ m: 2 }}>Sin resultados con los filtros aplicados.</Alert>
        )}
        {!loading && rows.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell>Código</TableCell>
                <TableCell>Razón social</TableCell>
                <TableCell>NIT</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Marketplace</TableCell>
                <TableCell>Creado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.aliadoID} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.codigo}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{r.razonSocial}</Typography>
                    {r.nombreComercial && (
                      <Typography variant="caption" sx={{ color: '#888' }}>{r.nombreComercial}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.nit}
                    {esNitTest(r.nit) && (
                      <Chip label="TEST" size="small" color="warning" sx={{ ml: 1, fontSize: '0.6rem', height: 16 }} />
                    )}
                  </TableCell>
                  <TableCell><Typography variant="caption">{r.tipoNegocio || '—'}</Typography></TableCell>
                  <TableCell>{chipEstado(r.estadoAprobacion)}</TableCell>
                  <TableCell>
                    {r.visibleMarketplace
                      ? <Chip label="Visible" size="small" color="success" variant="outlined" />
                      : <Chip label="Oculto"  size="small" variant="outlined" />
                    }
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {r.fechaCreacion ? new Date(r.fechaCreacion).toLocaleDateString() : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver detalle">
                      <IconButton size="small" onClick={() => abrirDetalle(r.aliadoID)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_,p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Por página"
        />
      </Paper>

      {/* Dialog de detalle */}
      <Dialog open={detalleAbierto} onClose={cerrarDetalle} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5,
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <StorefrontOutlinedIcon />
            <Box>
              <Typography sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {detalle?.razonSocial || 'Detalle del aliado'}
              </Typography>
              {detalle && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  Código {detalle.codigo} · NIT {detalle.nit}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton onClick={cerrarDetalle} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important' }}>
          {detalleLoading && (
            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
          )}
          {detalle && !detalleLoading && (
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                {chipEstado(detalle.estadoAprobacion)}
                {detalle.visibleMarketplace && (
                  <Chip label="Visible en marketplace" size="small" color="success" variant="outlined" />
                )}
                {detalle.tipoNegocio?.nombre && (
                  <Chip label={detalle.tipoNegocio.nombre} size="small" variant="outlined" />
                )}
              </Stack>

              {detalle.estadoAprobacion === 'RECHAZADO' && detalle.motivoRechazo && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <strong>Motivo del rechazo:</strong> {detalle.motivoRechazo}
                </Alert>
              )}

              {/* Datos generales */}
              <SectionTitle>Representante legal</SectionTitle>
              <RowKV k="Nombre" v={detalle.representanteLegal} />
              <RowKV k="Cédula" v={detalle.cedulaRepresentante} />

              <SectionTitle>Contacto</SectionTitle>
              <RowKV k="Email" v={detalle.email} />
              <RowKV k="Teléfono" v={detalle.telefono || '—'} />
              <RowKV k="Sitio web" v={detalle.sitioWeb || '—'} />

              <SectionTitle>Ubicación</SectionTitle>
              <RowKV k="Ciudad" v={detalle.municipio?.nombre || '—'} />
              <RowKV k="Dirección" v={detalle.direccionComercial} />
              <Box sx={{ mt: 1 }}>
                <UbicacionMapaPicker
                  latitud={detalle.latitud}
                  longitud={detalle.longitud}
                  alto={280}
                  soloLectura
                  ayuda={false}
                />
              </Box>

              <SectionTitle>Descuento en marketplace</SectionTitle>
              <RowKV k="Modo" v={detalle.usaDescuentoRed ? 'Descuento de la red' : 'Personalizado'} />
              {detalle.usaDescuentoRed
                ? <RowKV k="Snapshot" v={detalle.descuentoRedSnapshot != null ? `${detalle.descuentoRedSnapshot}%` : '—'} />
                : <RowKV k="Porcentaje" v={detalle.descuentoPersonalizado != null ? `${detalle.descuentoPersonalizado}%` : '—'} />
              }

              {detalle.descripcion && (
                <>
                  <SectionTitle>Descripción</SectionTitle>
                  <Typography variant="body2" sx={{ color: '#444' }}>{detalle.descripcion}</Typography>
                </>
              )}

              {Array.isArray(detalle.documentos) && detalle.documentos.length > 0 && (
                <>
                  <SectionTitle>Documentos verificados</SectionTitle>
                  <Stack spacing={1} sx={{ mb: 1 }}>
                    {detalle.documentos.map(d => (
                      <Paper key={d.aliadoDocumentoID} variant="outlined"
                        sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, borderRadius: 2 }}>
                        <DescriptionOutlinedIcon sx={{ color: '#2e7d32' }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {tipoDocumentoLabel(d.tipoDocumento)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                            {d.nombreArchivo}
                            {d.fechaSubida && ` · subido ${new Date(d.fechaSubida).toLocaleDateString()}`}
                          </Typography>
                          {d.notas && (
                            <Typography variant="caption" sx={{ color: '#888', fontStyle: 'italic' }}>
                              {d.notas}
                            </Typography>
                          )}
                        </Box>
                        <Button size="small" variant="outlined"
                          startIcon={<VisibilityOutlinedIcon />}
                          onClick={async () => {
                            try {
                              const resp = await getUrlFirmadaDocumento(d.url);
                              setVisor({
                                open: true,
                                url: resp.url,   // URL del proxy backend, no gs://
                                titulo: tipoDocumentoLabel(d.tipoDocumento),
                                nombre: detalle.razonSocial,
                              });
                            } catch (e) {
                              onToast?.(`No se pudo abrir: ${e?.message || 'error'}`, 'error');
                            }
                          }}>
                          Ver
                        </Button>
                      </Paper>
                    ))}
                  </Stack>
                </>
              )}

              <SectionTitle>Auditoría</SectionTitle>
              <RowKV k="Creado" v={detalle.fechaCreacion ? new Date(detalle.fechaCreacion).toLocaleString() : '—'} />
              <RowKV k="Última modificación" v={detalle.fechaModificacion ? new Date(detalle.fechaModificacion).toLocaleString() : '—'} />
              {detalle.fechaAprobacion && (
                <RowKV k="Aprobado" v={`${new Date(detalle.fechaAprobacion).toLocaleString()}${detalle.aprobadoPor ? ` por ${detalle.aprobadoPor}` : ''}`} />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid #eee' }}>
          {detalle?.estadoAprobacion === 'PENDIENTE_APROBACION' && (
            <>
              <Button variant="outlined" color="error"
                startIcon={<CancelOutlinedIcon />}
                onClick={() => setRechazoAbierto(true)}
                disabled={detalleAccion}>
                Rechazar
              </Button>
              <Button variant="contained" color="success"
                startIcon={detalleAccion ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
                onClick={accionAprobar}
                disabled={detalleAccion}>
                Aprobar
              </Button>
            </>
          )}
          {(detalle?.estadoAprobacion === 'APROBADO' || detalle?.estadoAprobacion === 'SUSPENDIDO') && (
            <>
              {detalle.estadoAprobacion === 'APROBADO' && (
                <>
                  <Button
                    variant={detalle.visibleMarketplace ? 'outlined' : 'contained'}
                    color={detalle.visibleMarketplace ? 'warning' : 'success'}
                    startIcon={detalle.visibleMarketplace ? <BlockOutlinedIcon /> : <StorefrontOutlinedIcon />}
                    onClick={pedirMotivoToggleMarketplace}
                    disabled={detalleAccion}
                  >
                    {detalle.visibleMarketplace ? 'Desactivar marketplace' : 'Activar marketplace'}
                  </Button>
                  <Button
                    variant={detalle.visiblePOS ? 'outlined' : 'contained'}
                    color={detalle.visiblePOS ? 'warning' : 'success'}
                    startIcon={detalle.visiblePOS ? <BlockOutlinedIcon /> : <PointOfSaleOutlinedIcon />}
                    onClick={pedirMotivoTogglePOS}
                    disabled={detalleAccion}
                  >
                    {detalle.visiblePOS ? 'Desactivar POS' : 'Activar POS'}
                  </Button>
                </>
              )}
              <Button
                variant="outlined"
                color={detalle.estadoAprobacion === 'APROBADO' ? 'warning' : 'success'}
                startIcon={<PowerSettingsNewIcon />}
                onClick={pedirMotivoToggleActivo}
                disabled={detalleAccion}
              >
                {detalle.estadoAprobacion === 'APROBADO' ? 'Desactivar' : 'Activar'}
              </Button>
            </>
          )}
          {detalle && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={abrirEliminar}
              disabled={detalleAccion}
              sx={{ ml: detalle.estadoAprobacion === 'APROBADO' ? 0 : 0 }}
            >
              Eliminar
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={cerrarDetalle}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Rechazo con motivo */}
      <Dialog open={rechazoAbierto} onClose={() => setRechazoAbierto(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Motivo del rechazo</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus multiline minRows={3} fullWidth
            value={motivoRechazo} onChange={e => setMotivoRechazo(e.target.value)}
            placeholder="Explica al aliado qué falta o por qué no se aprueba."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRechazoAbierto(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={accionRechazar} disabled={detalleAccion}>
            Confirmar rechazo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Eliminar aliado — confirmación fuerte con texto */}
      <Dialog open={eliminarAbierto} onClose={() => setEliminarAbierto(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#c62828' }}>Eliminar aliado</DialogTitle>
        <DialogContent>
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            Esta acción <strong>no se puede deshacer</strong>. Se eliminarán los
            registros del aliado, sus documentos y sus vínculos con usuarios
            <strong> solo en el entorno ecolab</strong>. El usuario del representante
            no se borra: solo se le quita el rol <em>Aliado</em> si este era su único
            negocio.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Vas a eliminar: <strong>{detalle?.razonSocial}</strong> ({detalle?.codigo})
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Para confirmar, escribí <strong>ELIMINAR</strong> abajo:
          </Typography>
          <TextField
            autoFocus fullWidth
            value={textoConfirmEliminar}
            onChange={e => setTextoConfirmEliminar(e.target.value)}
            placeholder="ELIMINAR"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEliminarAbierto(false)} disabled={detalleAccion}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={accionEliminar}
            disabled={detalleAccion || textoConfirmEliminar.trim() !== 'ELIMINAR'}
            startIcon={detalleAccion ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
          >
            Eliminar definitivamente
          </Button>
        </DialogActions>
      </Dialog>

      {/* Motivo genérico — para activar/desactivar aliado, marketplace, POS */}
      <Dialog open={motivoAbierto} onClose={() => !detalleAccion && setMotivoAbierto(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{motivoConfig?.titulo || 'Motivo'}</DialogTitle>
        <DialogContent>
          {detalle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              <strong>{detalle.razonSocial}</strong> ({detalle.codigo})
            </Typography>
          )}
          <TextField
            autoFocus multiline minRows={3} fullWidth
            value={motivoTexto}
            onChange={e => setMotivoTexto(e.target.value)}
            placeholder={motivoConfig?.placeholder || 'Motivo (opcional)'}
            helperText="Quedará registrado en la auditoría del sistema."
            sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMotivoAbierto(false)} disabled={detalleAccion}>Cancelar</Button>
          <Button
            variant="contained"
            color={motivoConfig?.color || 'primary'}
            onClick={confirmarMotivo}
            disabled={detalleAccion}
            startIcon={detalleAccion ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {motivoConfig?.accionLabel || 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visor de documentos — reutilizamos el componente existente */}
      <VisorDocumentoSeguro
        open={visor.open}
        onClose={() => setVisor(p => ({ ...p, open: false }))}
        urlDocumento={visor.url}
        titulo={visor.titulo}
        nombreAfiliado={visor.nombre}
      />
    </Box>
  );
}

const SectionTitle = ({ children }) => (
  <Typography variant="overline" sx={{
    display: 'block', color: '#2e7d32', fontWeight: 700,
    letterSpacing: '0.08em', mt: 2, mb: 0.5,
  }}>
    {children}
  </Typography>
);

const RowKV = ({ k, v }) => (
  <Grid container sx={{ fontSize: '0.85rem', py: 0.3 }}>
    <Grid item xs={4} sm={3} sx={{ color: '#666' }}>{k}</Grid>
    <Grid item xs={8} sm={9}>{v}</Grid>
  </Grid>
);

// Etiqueta humana para el tipo de documento del aliado
function tipoDocumentoLabel(tipo) {
  const map = {
    CEDULA_REPRESENTANTE: 'Cédula del representante legal',
    RUT:                  'RUT del negocio',
    CAMARA_COMERCIO:      'Cámara de Comercio',
    FORMULARIO_FIRMADO:   'Formulario firmado',
    OTRO:                 'Otro documento',
  };
  return map[tipo] || tipo;
}
